<script setup lang="ts">
import { computed } from 'vue'
import '@/assets/styles/election-summary/styles.css'

import type { ElectionSummary } from '@/modules/election/models/election-summary.model'
import type LeadingParty from '@/modules/election/models/leading_party.model'
import ElectoralCard from '@/modules/election/components/ElectoralCard.vue'
import MinorCandidateCard from './MinorCandidateCard.vue'
import type { PrimariesSummary } from '../models/primaries-summary.model'
import { STRINGS } from '../utils/strings'

interface Props {
  year: string
  summary: PrimariesSummary | null
}

const props = defineProps<Props>()

const firstLeadingPartyStyle = computed(() => ({
  color: props.summary?.firstLeadingParty.color as string
}))

const secondLeadingPartyStyle = computed(() => ({
  color: props.summary?.secondLeadingParty.color as string
}))

const firstLeadingPartyBarStyle = computed(() => ({
  width: `${props.summary?.firstLeadingParty.percentage}%`,
  backgroundColor: props.summary?.firstLeadingParty.color as string
}))

const secondLeadingPartyBarStyle = computed(() => ({
  width: `${props.summary?.secondLeadingParty.percentage}%`,
  backgroundColor: props.summary?.secondLeadingParty.color as string
}))

</script>
<template>
  <div class="election-results">
    <h1 class="title">{{ props.year }} {{ STRINGS.HEADERS.RESULTS }}</h1>
    <div class="results-bar">
      <!-- First Place -->
      <ElectoralCard
        :style="firstLeadingPartyStyle"
        :name="props.summary?.firstLeadingParty.name"
        party="NPP"
        :constituency-votes="props.summary?.firstLeadingParty.votes"
        :image-url="props.summary?.firstLeadingParty.image"
        :color="props.summary?.firstLeadingParty.color"
      />

      <!-- Bar Visualization -->
      <div class="results-section">
        <div class="bar">
          <div class="first-leading-party-bar" :style="firstLeadingPartyBarStyle">
            <span class="percentage-label">{{ props.summary?.firstLeadingParty.percentage }}%</span>
          </div>
        
          <!-- Minor candidates share of bar visualization is skipped to keep it clean, or could be added -->
          <div class="second-leading-party-bar" :style="secondLeadingPartyBarStyle">
            <span class="percentage-label">{{ props.summary?.secondLeadingParty.percentage }}%</span>
          </div>
        </div>
      </div>

      <!-- Second Place -->
      <ElectoralCard
        :style="secondLeadingPartyStyle"
        :name="props.summary?.secondLeadingParty.name"
        party="NPP"
        :constituency-votes="props.summary?.secondLeadingParty.votes"
        :image-url="props.summary?.secondLeadingParty.image"
        :color="props.summary?.secondLeadingParty.color"
      />

      <!-- Results Section / Margin -->
      <div class="results-section mb-3">
        <div class="win-margin">
          {{ STRINGS.HEADERS.WIN_MARGIN }}
          <span class="margin-number">{{ props.summary?.margin.toLocaleString() }}</span>
          {{ STRINGS.HEADERS.VOTES }} (<span class="margin-percentage"
            >{{ props.summary?.marginPercentage.toFixed(2) }}%</span
          >)
        </div>
      </div>

      <!-- Minor Candidates -->
      <div v-if="props.summary?.minorCandidates && props.summary.minorCandidates.length > 0" class="minor-candidates-section mt-3">
          <MinorCandidateCard 
            v-for="candidate in props.summary.minorCandidates" 
            :key="candidate.name" 
            :candidate="candidate" 
            class="minor-card"
          />
      </div>

    </div>
  </div>
</template>

<style scoped>
/* Inherit styles from global or copied styles.css, referencing them here just in case */
.candidate-image {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  margin-right: 13px;
}

.minor-candidates-section {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
}

.minor-card {
    flex: 1 1 200px; /* Grow, shrink, base width */
    max-width: 48%; /* Ensure 2 per row roughly if wanted, or let them flow */
}
</style>
